import React from "react"

import {
    Box,
    Button,
    Grid,
    IconButton,
    Stack,
    Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import { Trans } from "@lingui/macro"

import { Document } from "models"
import {
    isDocumentImageLike,
    resolveDocumentLabel,
} from "utils/document.utils"

interface AssetDocumentsSectionProps {
    documents: Document[]
    userIsBrand: boolean
    assetId: string
    onOpenUploadModal: () => void
    onOpenDeleteModal: (documentId: string) => void
    formatDate: (value?: Date | string | null) => string
}

export const AssetDocumentsSection = ({
    documents,
    userIsBrand,
    assetId,
    onOpenUploadModal,
    onOpenDeleteModal,
    formatDate,
}: AssetDocumentsSectionProps) => {
    return (
        <Box sx={{ border: "1px solid #d4d9e3", backgroundColor: "white" }}>
            <Box sx={{ backgroundColor: "#173e8d", color: "white", px: 3, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            <Trans id="asset-details.documents.title">Documenti immobile</Trans>
                        </Typography>
                        <Typography>
                            <Trans id="asset-details.documents.count">{documents.length} documenti disponibili</Trans>
                        </Typography>
                    </Box>
                    {!userIsBrand && (
                        <Button
                            variant="contained"
                            onClick={onOpenUploadModal}
                            sx={{ textTransform: "none", backgroundColor: "#00a3e5" }}
                        >
                            <Trans id="asset-details.documents.upload">Carica documenti</Trans>
                        </Button>
                    )}
                </Stack>
            </Box>

            <Box sx={{ p: 2 }}>
                {documents.length === 0 ? (
                    <Typography color="text.secondary" sx={{ p: 2 }}>
                        <Trans id="asset-details.documents.empty">Nessun documento disponibile.</Trans>
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {documents.map((document) => {
                            const imageLike = isDocumentImageLike(document)
                            const iconColor = imageLike ? "#5e9db7" : "#bf7f9e"
                            const documentUrl = `/api/asset/${assetId}/document/${document.id}`

                            return (
                                <Grid item xs={12} md={6} key={document.id}>
                                    <Box sx={{ border: "1px solid #d4d9e3", backgroundColor: "#f9fafc", px: 2, py: 1.5 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: iconColor }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography fontWeight={600} noWrap>{document.name || "-"}</Typography>
                                                <Typography color="text.secondary" variant="body2">
                                                    {`${resolveDocumentLabel(document)} • ${formatDate(document.uploadDate)}`}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={documentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: "#646b76" }}
                                            >
                                                <VisibilityOutlinedIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={documentUrl}
                                                download
                                                sx={{ color: "#646b76" }}
                                            >
                                                <DownloadOutlinedIcon fontSize="small" />
                                            </IconButton>
                                            {!userIsBrand && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onOpenDeleteModal(document.id)}
                                                    sx={{ color: "#646b76" }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>
                            )
                        })}
                    </Grid>
                )}
            </Box>
        </Box>
    )
}
